import React, { useState, useCallback } from 'react';
import { useInitialFormState } from '../../hooks/useInitialFormState';
import ImageUploadPreview from '../ui/ImageUpload/ImageUploadPreview';
import { uploadImage, fetchServiceCategories } from '../../api/hotelinfoApi';
import { deleteRemovedImages } from '../../utils/imageUtils';
import {
  Drawer,
  TextField, Button, FormControlLabel, Checkbox,
  Select, MenuItem, InputLabel, FormControl, Typography, Box, CircularProgress, Divider,
  Autocomplete, createFilterOptions, Chip
} from '@mui/material';

// Category Autocomplete filter helper
const catFilter = createFilterOptions({ trim: true, ignoreCase: true });

// Move the handleImageUrlsChange creator outside the component
const createHandleImageUrlsChange = (setForm) =>
  (updatedUrls) => {
    setForm(prev => ({ ...prev, imageUrls: updatedUrls }));
  };

const slugify = (s = '') => s.toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');

const ServiceFormModal = ({
  open,
  onClose,
  initialData,
  onSubmit,
  editingId
}) => {
  const [form, setForm] = useState({});
  const [uploadingImages, setUploadingImages] = useState(false);
  useInitialFormState(initialData, setForm, ['imageUrls']);

  // Category autocomplete state
  const [catInput, setCatInput] = useState('');
  const [catOptions, setCatOptions] = useState([]);
  const [catLoading, setCatLoading] = useState(false);

  // Debounced category fetch
  React.useEffect(() => {
    let active = true;
    if (!open) return;
    const q = (catInput || form.category || '').trim();
    // Skip fetch on empty
    if (!q) { setCatOptions([]); return; }
    setCatLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await fetchServiceCategories(q, initialData?.propertyId || form.propertyId);
        if (!active) return;
        setCatOptions(Array.isArray(data) ? data : []);
      } catch {
        if (active) setCatOptions([]);
      } finally {
        if (active) setCatLoading(false);
      }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [open, catInput, form.category, initialData?.propertyId, form.propertyId]);

  React.useEffect(() => {
    setForm(prev => {
      const next = { ...prev };
      next.transportation = next.transportation || {};
      // Initialize options array if missing
      if (!Array.isArray(next.transportation.options)) {
        next.transportation.options = [];
      }
      // Attempt a light migration from legacy fields if present and no options yet
      const t = next.transportation;
      if (next.transportation.options.length === 0) {
        if (t?.tukTuk?.price1to2 || t?.tukTuk?.price3to4) {
          next.transportation.options.push({
            key: 'tuktuk',
            label: 'Tuk-Tuk',
            tiers: [
              ...(t?.tukTuk?.price1to2 ? [{ name: '1-2 pax', price: Number(t.tukTuk.price1to2) }] : []),
              ...(t?.tukTuk?.price3to4 ? [{ name: '3-4 pax', price: Number(t.tukTuk.price3to4) }] : []),
            ],
            extras: [
              ...(t?.tukTuk?.extraSunrise ? [{ key: 'sunrise', label: 'Sunrise', price: Number(t.tukTuk.extraSunrise) }] : []),
              ...(t?.tukTuk?.extraBackTown ? [{ key: 'backTown', label: 'Back to Town', price: Number(t.tukTuk.extraBackTown) }] : []),
            ],
          });
        }
        if (t?.car?.price) {
          next.transportation.options.push({
            key: 'car',
            label: 'Car',
            price: Number(t.car.price),
            extras: [
              ...(t?.car?.extraSunrise ? [{ key: 'sunrise', label: 'Sunrise', price: Number(t.car.extraSunrise) }] : []),
              ...(t?.car?.extraBackTown ? [{ key: 'backTown', label: 'Back to Town', price: Number(t.car.extraBackTown) }] : []),
            ],
          });
        }
        if (t?.van?.price) {
          next.transportation.options.push({
            key: 'van',
            label: 'Van',
            price: Number(t.van.price),
            extras: [
              ...(t?.van?.extraSunrise ? [{ key: 'sunrise', label: 'Sunrise', price: Number(t.van.extraSunrise) }] : []),
              ...(t?.van?.extraBackTown ? [{ key: 'backTown', label: 'Back to Town', price: Number(t.van.extraBackTown) }] : []),
            ],
          });
        }
      }
      return next;
    });
  }, [open]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (section, field, value) => {
    setForm(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleTransportChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      transportation: {
        ...prev.transportation,
        [field]: value
      }
    }));
  };

  const ensureTransport = () => {
    setForm(prev => ({
      ...prev,
      transportation: { ...(prev.transportation || {}), options: Array.isArray(prev.transportation?.options) ? prev.transportation.options : [] }
    }));
  };

  const addOption = () => {
    ensureTransport();
    setForm(prev => ({
      ...prev,
      transportation: {
        ...prev.transportation,
        options: [...(prev.transportation?.options || []), { key: `opt-${Date.now()}`, label: '', price: '', tiers: [], extras: [] }]
      }
    }));
  };

  const removeOption = (idx) => {
    setForm(prev => ({
      ...prev,
      transportation: {
        ...prev.transportation,
        options: (prev.transportation?.options || []).filter((_, i) => i !== idx)
      }
    }));
  };

  const updateOptionField = (idx, field, value) => {
    setForm(prev => {
      const options = [...(prev.transportation?.options || [])];
      options[idx] = { ...options[idx], [field]: value };
      if (field === 'label' && !options[idx].key) options[idx].key = slugify(value);
      return { ...prev, transportation: { ...prev.transportation, options } };
    });
  };

  const addTier = (idx) => {
    setForm(prev => {
      const options = [...(prev.transportation?.options || [])];
      const opt = { ...(options[idx] || {}) };
      opt.tiers = Array.isArray(opt.tiers) ? [...opt.tiers, { name: '', price: '' }] : [{ name: '', price: '' }];
      options[idx] = opt;
      return { ...prev, transportation: { ...prev.transportation, options } };
    });
  };

  const updateTier = (idx, tIdx, field, value) => {
    setForm(prev => {
      const options = [...(prev.transportation?.options || [])];
      const opt = { ...(options[idx] || {}) };
      const tiers = [...(opt.tiers || [])];
      tiers[tIdx] = { ...tiers[tIdx], [field]: value };
      opt.tiers = tiers;
      options[idx] = opt;
      return { ...prev, transportation: { ...prev.transportation, options } };
    });
  };

  const removeTier = (idx, tIdx) => {
    setForm(prev => {
      const options = [...(prev.transportation?.options || [])];
      const opt = { ...(options[idx] || {}) };
      opt.tiers = (opt.tiers || []).filter((_, i) => i !== tIdx);
      options[idx] = opt;
      return { ...prev, transportation: { ...prev.transportation, options } };
    });
  };

  const addExtra = (idx) => {
    setForm(prev => {
      const options = [...(prev.transportation?.options || [])];
      const opt = { ...(options[idx] || {}) };
      opt.extras = Array.isArray(opt.extras) ? [...opt.extras, { key: '', label: '', price: '' }] : [{ key: '', label: '', price: '' }];
      options[idx] = opt;
      return { ...prev, transportation: { ...prev.transportation, options } };
    });
  };

  const updateExtra = (idx, eIdx, field, value) => {
    setForm(prev => {
      const options = [...(prev.transportation?.options || [])];
      const opt = { ...(options[idx] || {}) };
      const extras = [...(opt.extras || [])];
      extras[eIdx] = { ...extras[eIdx], [field]: value };
      if (field === 'label' && !extras[eIdx].key) extras[eIdx].key = slugify(value);
      opt.extras = extras;
      options[idx] = opt;
      return { ...prev, transportation: { ...prev.transportation, options } };
    });
  };

  const removeExtra = (idx, eIdx) => {
    setForm(prev => {
      const options = [...(prev.transportation?.options || [])];
      const opt = { ...(options[idx] || {}) };
      opt.extras = (opt.extras || []).filter((_, i) => i !== eIdx);
      options[idx] = opt;
      return { ...prev, transportation: { ...prev.transportation, options } };
    });
  };

  const handleSubmit = () => {
    const toNumber = (v) => (v === '' || v === null || v === undefined ? undefined : Number(v));

    const cleaned = { ...form };

    // Normalize features
    if (Array.isArray(cleaned.features)) {
      const uniqueFeatures = Array.from(new Set(cleaned.features.map((f) => String(f).trim()).filter(Boolean)));
      cleaned.features = uniqueFeatures;
    }

    // Normalize tags
    if (Array.isArray(cleaned.tags)) {
      cleaned.tags = cleaned.tags.map(t => String(t).trim()).filter(Boolean);
    }

    // Ensure transportation shape
    if (cleaned.transportation && Array.isArray(cleaned.transportation.options)) {
      cleaned.transportation = { ...cleaned.transportation };
      cleaned.transportation.options = cleaned.transportation.options
        .map(opt => {
          const o = { ...opt };
          o.key = (o.key || '').trim();
          o.label = (o.label || '').trim();
          if (o.price !== undefined) o.price = toNumber(o.price);
          if (Array.isArray(o.tiers)) {
            o.tiers = o.tiers
              .map(t => ({ name: (t?.name || '').trim(), price: toNumber(t?.price) }))
              .filter(t => t.name && (typeof t.price === 'number' && !isNaN(t.price)));
          }
          if (Array.isArray(o.extras)) {
            o.extras = o.extras
              .map(ex => ({ key: (ex?.key || '').trim() || slugify(ex?.label || ''), label: (ex?.label || '').trim(), price: toNumber(ex?.price) }))
              .filter(ex => ex.label && ex.key && (typeof ex.price === 'number' && !isNaN(ex.price)));
          }
          // Drop option if missing label/key
          if (!o.label || !o.key) return null;
          // Drop empty arrays
          if (o.tiers && o.tiers.length === 0) delete o.tiers;
          if (o.extras && o.extras.length === 0) delete o.extras;
          return o;
        })
        .filter(Boolean);
    }

    // Coerce numeric top-level fields
    cleaned.price = toNumber(cleaned.price);
    cleaned.priority = toNumber(cleaned.priority) ?? 0;

    onSubmit(cleaned);

    // Delay image cleanup to prevent blocking the UI
    setTimeout(() => {
      deleteRemovedImages(initialData?.imageUrls || [], cleaned.imageUrls || []);
    }, 500);
  };

  const handleImageUrlsChange = useCallback((updatedUrls) => {
    setForm(prev => ({ ...prev, imageUrls: updatedUrls }));
  }, [setForm]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 600, p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {editingId ? 'Edit' : 'Add'} Services
        </Typography>

        <Autocomplete
          freeSolo
          options={catOptions}
          value={form.category || ''}
          inputValue={catInput}
          onInputChange={(_, val) => setCatInput(val)}
          filterOptions={(options, params) => {
            const filtered = catFilter(options, params);
            const inputValue = (params?.inputValue || '').trim();
            if (!inputValue) return filtered;
            const exists = (options || []).some(o => (o || '').toLowerCase() === inputValue.toLowerCase());
            if (!exists) {
              filtered.push(`Create "${inputValue}"`);
            }
            return filtered;
          }}
          onChange={(_, val) => {
            let raw = '';
            if (typeof val === 'string') raw = val;
            else if (val && typeof val.inputValue === 'string') raw = val.inputValue;
            else raw = String(val || '');

            raw = raw.trim();
            if (!raw) return handleChange('category', '');

            // If user clicked the synthetic "Create \"...\"" option, strip the prefix/suffix
            const createMatch = raw.match(/^Create\s+"(.+)"$/);
            const v = createMatch ? createMatch[1] : raw;

            // If matches existing option case-insensitively, coerce to the canonical existing string
            const match = (catOptions || []).find(o => (o || '').toLowerCase() === v.toLowerCase());
            handleChange('category', match || v);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Category"
              margin="normal"
              fullWidth
              helperText={catLoading ? 'Loading categories…' : 'Type to search existing categories or add a new one'}
            />
          )}
        />

        <TextField
          fullWidth
          label="Title"
          value={form.title || ''}
          onChange={e => handleChange('title', e.target.value)}
          margin="normal"
        />

        <TextField
          fullWidth
          label="Description"
          multiline
          rows={3}
          value={form.description || ''}
          onChange={e => handleChange('description', e.target.value)}
          margin="normal"
        />

        <TextField
          fullWidth
          label="Included"
          value={form.included || ''}
          onChange={e => handleChange('included', e.target.value)}
          margin="normal"
        />

        <TextField
          fullWidth
          label="Not Included"
          value={form.notIncluded || ''}
          onChange={e => handleChange('notIncluded', e.target.value)}
          margin="normal"
        />

        <Autocomplete
          multiple
          freeSolo
          options={FEATURE_SUGGESTIONS}
          filterOptions={(options, params) => {
            const filtered = featureFilter(options, params);
            const inputValue = (params?.inputValue || '').trim();
            if (!inputValue) return filtered;
            const exists = (options || []).some((opt) => (opt || '').toLowerCase() === inputValue.toLowerCase());
            if (!exists) {
              filtered.push(`Add "${inputValue}"`);
            }
            return filtered;
          }}
          value={Array.isArray(form.features) ? form.features : []}
          onChange={(_, value) => {
            const next = Array.from(new Set((value || []).map((item) => {
              if (!item) return '';
              if (typeof item === 'string') {
                const createMatch = item.match(/^Add\s+"(.+)"$/);
                return (createMatch ? createMatch[1] : item).trim();
              }
              if (item && typeof item.inputValue === 'string') return item.inputValue.trim();
              return String(item || '').trim();
            }).filter(Boolean)));
            handleChange('features', next);
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip variant="outlined" label={option} {...getTagProps({ index })} key={`${option}-${index}`} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Features"
              margin="normal"
              helperText="Select or type feature highlights (e.g. Private, Full Day)."
            />
          )}
        />

        <TextField
          fullWidth
          label="Tags (comma separated)"
          value={form.tags?.join(', ') || ''}
          onChange={e => handleChange('tags', e.target.value.split(',').map(tag => tag.trim()))}
          margin="normal"
        />

        <TextField
          fullWidth
          label="Duration (e.g. 3 hours)"
          value={form.duration || ''}
          onChange={e => handleChange('duration', e.target.value)}
          margin="normal"
        />

        <TextField
          fullWidth
          label="From Price (display only)"
          type="number"
          value={form.price || ''}
          onChange={e => handleChange('price', e.target.value)}
          margin="normal"
          helperText="Shown as a starting price on listings. Cart totals come from selected transportation + extras."
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={form.bookingRequired || false}
              onChange={e => handleChange('bookingRequired', e.target.checked)}
            />
          }
          label="Booking Required"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={form.isFeatured || false}
              onChange={e => handleChange('isFeatured', e.target.checked)}
            />
          }
          label="Featured Service"
        />

        <TextField
          fullWidth
          label="Priority"
          type="number"
          value={form.priority || 0}
          onChange={e => handleChange('priority', parseInt(e.target.value))}
          margin="normal"
        />

        <TextField
          fullWidth
          type="time"
          label="Available From"
          value={form.availableHours?.from || ''}
          onChange={e =>
            setForm(prev => ({
              ...prev,
              availableHours: {
                ...prev.availableHours,
                from: e.target.value
              }
            }))
          }
          margin="normal"
        />

        <TextField
          fullWidth
          type="time"
          label="Available To"
          value={form.availableHours?.to || ''}
          onChange={e =>
            setForm(prev => ({
              ...prev,
              availableHours: {
                ...prev.availableHours,
                to: e.target.value
              }
            }))
          }
          margin="normal"
        />

        <FormControl fullWidth margin="normal">
          <InputLabel>Status</InputLabel>
          <Select
            value={form.status || 'active'}
            onChange={e => handleChange('status', e.target.value)}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={form.transportation?.allowChoice || false}
              onChange={e => handleTransportChange('allowChoice', e.target.checked)}
            />
          }
          label="Allow user to choose transportation"
        />

        <Box mt={2}>
          <Typography variant="h6">Transport Options</Typography>

          {(form.transportation?.options || []).map((opt, idx) => (
            <Box key={idx} sx={{ p: 2, border: '1px solid #eee', borderRadius: 1, mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Label"
                  value={opt.label || ''}
                  onChange={e => updateOptionField(idx, 'label', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Key"
                  value={opt.key || ''}
                  onChange={e => updateOptionField(idx, 'key', slugify(e.target.value))}
                  helperText="machine key"
                  sx={{ width: 200 }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Pricing</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Flat Price (optional)"
                  type="number"
                  value={opt.price ?? ''}
                  onChange={e => updateOptionField(idx, 'price', e.target.value)}
                  sx={{ width: 240 }}
                />
                <Button variant="outlined" onClick={() => addTier(idx)}>Add Tier</Button>
              </Box>

              {(opt.tiers || []).map((t, tIdx) => (
                <Box key={tIdx} sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <TextField
                    label="Tier Name"
                    value={t.name || ''}
                    onChange={e => updateTier(idx, tIdx, 'name', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Tier Price"
                    type="number"
                    value={t.price ?? ''}
                    onChange={e => updateTier(idx, tIdx, 'price', e.target.value)}
                    sx={{ width: 200 }}
                  />
                  <Button color="error" onClick={() => removeTier(idx, tIdx)}>Remove</Button>
                </Box>
              ))}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Extras</Typography>
              <Button variant="outlined" onClick={() => addExtra(idx)}>Add Extra</Button>
              {(opt.extras || []).map((ex, eIdx) => (
                <Box key={eIdx} sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <TextField
                    label="Label"
                    value={ex.label || ''}
                    onChange={e => updateExtra(idx, eIdx, 'label', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Key"
                    value={ex.key || ''}
                    onChange={e => updateExtra(idx, eIdx, 'key', slugify(e.target.value))}
                    helperText="machine key"
                    sx={{ width: 200 }}
                  />
                  <TextField
                    label="Price"
                    type="number"
                    value={ex.price ?? ''}
                    onChange={e => updateExtra(idx, eIdx, 'price', e.target.value)}
                    sx={{ width: 160 }}
                  />
                  <Button color="error" onClick={() => removeExtra(idx, eIdx)}>Remove</Button>
                </Box>
              ))}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button color="error" onClick={() => removeOption(idx)}>Delete Option</Button>
              </Box>
            </Box>
          ))}

          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={addOption}>Add Transport Option</Button>
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Images</Typography>
          <ImageUploadPreview
            initialUrls={form.imageUrls || []}
            onChange={handleImageUrlsChange}
            uploadImageFn={async (file, onProgress) => {
              setUploadingImages(true);
              const url = await uploadImage(file, onProgress);
              setUploadingImages(false);
              return url;
            }}
          />
        </Box>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ ml: 1 }}
            disabled={uploadingImages}
            startIcon={uploadingImages ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ServiceFormModal;
const FEATURE_SUGGESTIONS = [
  'Private',
  'Shared',
  'Full Day',
  'Half Day',
  'Sunrise',
  'Sunset',
  'Family Friendly',
  'Adventure',
  'Cultural',
  'Guided',
  'Self-Guided'
];

const featureFilter = createFilterOptions({ trim: true, ignoreCase: true });
